import { cn } from '@/lib/utils';
import StarNode from '@/widget/StarNode';
import CopyNode from '@/widget/CopyNode';
import { Search } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import TokenAvatar from '@/widget/TokenAvatar';
import CustomToolTips from '@/widget/CustomToolTips';
import { ISignal } from '@/request/signal/signal.response.interface';
import { formatCryptoPrice, formatTextEllipsis, formatTimeAgo, formatValueWithCompactNumber } from '@/lib/format';
import BigNumber from 'bignumber.js';
import { useSignalStore } from '@/page/token/signal/store';
import { useBoomWallet } from '@boomeme/wallet-sdk';
import { Trans, useTranslation } from 'react-i18next';
import { useAppEventBusContext } from '@/context/eventBusContext';
import { SubscribeEvent } from '@/interface/event';
import { SOL_TOKEN_ADDRESS } from '@/config';
import { getTransaction } from '@/lib/connection';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '@/store';
import SafeSheet from '../SafeSheet';
import TradeSheet from '../TradeSheet';
import NoticeSheet from '../NoticeSheet';
import { toast } from '@/widget/BoomToast';

export type HandleType = 'safe' | 'trade' | 'notice';

interface IProps {
  data: ISignal;
}

export interface IPriceUpdate {
  address: string;
  highestPrice: number | string;
  price: string;
  type: 'buy' | 'sell';
}

interface IHolderUpdate {
  address: string;
  holders: number;
}
interface ILiquidityUpdate {
  address: string;
  liquidity: number;
}

export default (props: IProps) => {
  const { data } = props;
  const {
    fwClass,
    chainCoinBalance,
    lang,
    chainInfo: { path },
  } = useAppStore();
  const { t } = useTranslation();
  const { publish } = useAppEventBusContext();
  const navigate = useNavigate();
  const { isConnected, transactions, chain, evmTransactions } = useBoomWallet();
  const { holdingList, bscSignalTradingSetting, solSignalTradingSetting } = useSignalStore();
  const targetChainTradeSetting = chain == 'bsc' ? bscSignalTradingSetting : solSignalTradingSetting;
  const { quickBuys, quickSells, fee, feeAuto, slippage, slippageAuto } = targetChainTradeSetting;
  const { subscribe } = useAppEventBusContext();

  const quickConfig = [
    {
      value: quickBuys[0],
      label: quickBuys[0],
      type: 'buy',
    },
    {
      value: quickBuys[1],
      label: quickBuys[1],
      type: 'buy',
    },
    {
      value: quickBuys[2],
      label: quickBuys[2],
      type: 'buy',
    },
    {
      value: Number(quickSells[0]) / 100,
      label: quickSells[0] + '%',
      type: 'sell',
    },
    {
      value: Number(quickSells[1]) / 100,
      label: quickSells[1] + '%',
      type: 'sell',
    },
    {
      value: Number(quickSells[2]) / 100,
      label: quickSells[2] + '%',
      type: 'sell',
    },
  ];

  const alertTimeMc = BigNumber(data.totalSupply).times(data.alert.price).toString();

  const handleEvmTransaction = async (isTradeBuy: boolean, amount: string) => {
    try {
      const { address, dex, isCompleted, decimals, symbol } = data;
      if (!address) return;
      const tradeTypeParams = dex === 1 ? (isCompleted ? 'pancake' : 'fourmeme') : 'pancake';
      const isPumpFromFourmeme = dex == 1 ? true : false;
      if (isTradeBuy) {
        const hash = await evmTransactions?.buy?.({
          outputToken: {
            outputTokenAddress: address,
            outputTokenDecimals: decimals,
            outputTokenName: symbol,
          },
          inputTokenAmount: Number(amount),
          tradeType: tradeTypeParams,
          isPumpFromFourmeme,
          ...(!slippageAuto && { slippage: Number(slippage) * 100 }),
          ...(!feeAuto && { gasFee: (Number(fee) * 1e9).toString() }),
        });
        if (hash) {
          publish(SubscribeEvent.Update_Balance);
        }
      } else {
        const hash = await evmTransactions?.sell?.({
          inputToken: {
            inputTokenAddress: address,
            inputTokenDecimals: decimals,
            inputTokenName: symbol,
          },
          inputTokenAmount: Number(amount),
          tradeType: tradeTypeParams,
          isPumpFromFourmeme,
          ...(!slippageAuto && { slippage: Number(slippage) * 100 }),
          ...(!feeAuto && { gasFee: (Number(fee) * 1e9).toString() }),
        });
        if (hash) {
          publish(SubscribeEvent.Update_Balance);
        }
      }
    } catch (error) {
      throw error;
    }
  };

  const handlerSolanaTransaction = async (isTradeBuy: boolean, amount: string) => {
    const decimals = isTradeBuy ? 9 : data?.decimals;
    const [inputTokenAddress, outputTokenAddress] = isTradeBuy ? [SOL_TOKEN_ADDRESS, data.address] : [data.address, SOL_TOKEN_ADDRESS];

    const hash = await transactions.trade({
      inputTokenAddress,
      outputTokenAddress,
      tradeType: data.dex == 1 && data.isCompleted ? 'jup' : data.dex == 1 && !data.isCompleted ? 'pump' : 'jup',
      amountIn: BigNumber(amount)
        .times(10 ** decimals)
        .toFixed(0),
      ...(!feeAuto && { priorityFee: BigNumber(fee).times(1e9).toString() }),
      ...(!slippageAuto && { slippage: Math.round(Number(slippage) * 100) }),
    });
    if (hash) {
      const res = await getTransaction(hash);
      if (res) {
        publish(SubscribeEvent.Update_Balance);
      }
    }
  };
  const onQuickTrade = async (index: number, tokenAddress: string) => {
    if (!isConnected) {
      publish(SubscribeEvent.Open_Wallet_Modal, true);
      return;
    }
    const tradeConfig = quickConfig[index];
    const isTradeBuy = tradeConfig.type === 'buy';
    const token = holdingList?.find((item) => item.tokenAddress.toLowerCase() === tokenAddress.toLowerCase());

    if (isTradeBuy) {
      if (BigNumber(chainCoinBalance).lt(tradeConfig.value)) {
        toast.show({ message: t('Signal.notBalance'), status: 'error' });
        return;
      }
    } else {
      if (token == undefined) {
        toast.show({ message: t('Signal.notBalance'), status: 'error' });
        return;
      }
    }
    const amount = isTradeBuy
      ? tradeConfig.value.toString()
      : BigNumber(tradeConfig.value)
          .times(token?.amount || 0)
          .toFixed();
    if (chain == 'solana') {
      handlerSolanaTransaction(isTradeBuy, amount);
    } else {
      handleEvmTransaction(isTradeBuy, amount);
    }
  };

  const [newPriceInfo, setNewPriceInfo] = useState<IPriceUpdate>({
    address: data.address,
    highestPrice: data.highestPrice,
    price: data.now.price,
    type: 'buy',
  });

  const [tradeType, setTradeType] = useState<'buy' | 'sell' | ''>('');

  useEffect(() => {
    const unsubscribe = subscribe(SubscribeEvent.Update_Signal_Price, (data: IPriceUpdate) => {
      if (data.address) {
        setNewPriceInfo(data);
      }
    });
    return () => unsubscribe();
  }, []);

  const realTimePrice = useMemo(() => {
    if (data.address.toLowerCase() === newPriceInfo.address.toLowerCase()) {
      setTradeType(newPriceInfo.type);
      return newPriceInfo.price;
    } else {
      return data.now.price;
    }
  }, [data, newPriceInfo]);

  useEffect(() => {
    if (tradeType) {
      const timer = setTimeout(() => {
        setTradeType('');
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [tradeType]);

  const greenGradient = 'linear-gradient(90deg, rgba(39, 39, 42, 0.00) -22.77%, rgba(14, 203, 129, 0.20) 100%)';
  const redGradient = 'linear-gradient(90deg, rgba(39, 39, 42, 0.00) -22.77%, rgba(255, 68, 68, 0.20) 100%)';

  const [holdersInfo, setHolderInfo] = useState<IHolderUpdate>({
    address: '',
    holders: 0,
  });
  useEffect(() => {
    const unsubscribe = subscribe(SubscribeEvent.Update_Signal_Holders, (data: IHolderUpdate) => {
      if (data.holders) {
        setHolderInfo(data);
      }
    });

    return () => unsubscribe();
  }, []);

  const [liquidityInfo, setLiquidityInfo] = useState<ILiquidityUpdate>({
    address: '',
    liquidity: 0,
  });

  useEffect(() => {
    const unsubscribe = subscribe(SubscribeEvent.Update_Signal_Liquidity, (data: ILiquidityUpdate) => {
      if (data.address) {
        setLiquidityInfo(data);
      }
    });

    return () => unsubscribe();
  }, []);

  const realTimeHolder = useMemo(() => {
    if (data.address.toLowerCase() === holdersInfo.address.toLowerCase()) {
      return holdersInfo.holders;
    } else {
      return data.now.holders;
    }
  }, [data, holdersInfo]);

  const realTimeLiquidity = useMemo(() => {
    let liquidity;
    if (data.address.toLowerCase() === liquidityInfo.address.toLowerCase()) {
      liquidity = liquidityInfo.liquidity;
    } else {
      liquidity = data.now.liquidity;
    }
    return BigNumber(liquidity).eq(0) ? '--' : formatValueWithCompactNumber(liquidity, true);
  }, [data, liquidityInfo]);

  const alertLiquidity = useMemo(() => {
    return BigNumber(data.alert.liquidity).eq(0) ? '--' : formatValueWithCompactNumber(data.alert.liquidity, true);
  }, [data.alert.liquidity]);

  const nowTimeMc = BigNumber(data.totalSupply).times(realTimePrice).toString();

  const safeInfo = {
    top10UserPercent: data?.top10UserPercent || data?.top10Percent || 0,
    isOpenSource: data?.isOpenSource || false,
    isHoneyPot: data?.isHoneyPot || false,
    isRenounced: data?.isRenounced || false,
    burnt: data?.burnt || false,
    blacklist: data?.blacklist || false,
    nomint: data?.nomint || false,
  };

  const tokenInfo = {
    logo: data.logo,
    address: data.address,
    symbol: data.symbol,
    createTime: data.createTime,
    timeRange: data.timeRange,
    tradeCount: data.walletCount,
    totalSupply: data.totalSupply,
    initPrice: data.now.price,
  };

  const noticeInfo = {
    highestPrice: data.highestPrice,
    firstAlertPrice: data.firstAlertPrice || '0',
    logo: data.logo,
    address: data.address,
    symbol: data.symbol,
    createTime: data.createTime,
    totalSupply: data.totalSupply,
  };

  /**
   * 获取序数词后缀
   * @param n 数字
   * @param lang 语言
   * @returns 带有序数词后缀的字符串
   */
  function getOrdinalSuffix(n: number, lang: string = 'en'): string {
    if (lang === 'zh-HK' || lang === 'zh-CN') {
      return n.toString();
    }

    // 英文序数词处理
    if (n <= 0) return n.toString();

    const j = n % 10;
    const k = n % 100;

    if (j === 1 && k !== 11) {
      return n + 'st';
    }
    if (j === 2 && k !== 12) {
      return n + 'nd';
    }
    if (j === 3 && k !== 13) {
      return n + 'rd';
    }
    return n + 'th';
  }

  const onFoucsTime = () => {
    publish(SubscribeEvent.Focus_Token, data.address);
  };

  const currentMemePlatformIcon = chain == 'bsc' ? '/icon/Fourmeme.svg' : '/icon/Pump.svg';

  const onJumpExplore = (e: React.MouseEvent) => {
    e.stopPropagation();
    const link = chain == 'bsc' ? `https://www.four.meme/token/${data.address}` : `https://pump.fun/coin/${data.address}`;
    window.open(link);
  };

  return (
    <div className="relative p-[8px] rounded-[12px] h-[360px] dark:bg-[#27272ACC] shadow-[0px_7px_20px_0px_rgba(196,196,196,0.30)] dark:shadow-none">
      <div className="absolute top-0 left-0 w-[22px] h-[18px]">
        <div className="bg-[url(/icon/alertTimes.svg)] bg-cover bg-no-repeat w-[32px] h-[26px] text-center leading-[26px]" onClick={onFoucsTime}>
          <span className={cn('text-[#18181B] text-[16px]', fwClass)}>{data.pushTimes}</span>
        </div>
      </div>
      <div className="border-b-[1px] border-borderColor pb-[8px] pl-[26px] flex justify-between">
        <CustomToolTips
          wrapperClass="bg-secondaryBgColor p-0 border-none"
          triggerNode={<span className="text-[12px]">{formatTimeAgo(data?.timestamp, { style: 'shortAgo', lang })}</span>}
          content={
            <div className="bg-secondaryBgColor p-[12px]  text-primaryTextColor border-none ">
              <Trans
                i18nKey={'Signal.alertTips'}
                components={[<span className="text-themeColor"></span>, <span className="text-themeColor"></span>]}
                values={{
                  count: getOrdinalSuffix(data.pushTimes, lang),
                  time: formatTimeAgo(data?.timestamp, { style: 'shortAgo', lang }),
                }}
              />
            </div>
          }
        />
        <SafeSheet safeInfo={safeInfo} />
      </div>
      <div className="flex justify-between items-center mt-[8px]">
        <div className="flex items-center gap-x-[12px]">
          <TokenAvatar className="size-[30px]" address={data.address} logo={data.logo} onClick={() => navigate(`/${path}/trade/${data.address}`)} />
          <div>
            <div className="flex items-center gap-x-[16px]">
              <span
                className={cn(fwClass, 'text-[20px] text-nowrap max-w-[100px] overflow-hidden text-ellipsis')}
                onClick={() => navigate(`/${path}/trade/${data.address}`)}
              >
                {data.symbol}
              </span>
              {data.dex == 1 && <img src={currentMemePlatformIcon} className="size-[18px]" onClick={onJumpExplore} />}
              <Search
                className="size-[18px] opacity-[0.4]"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(`https://x.com/search?q=${data.address}`);
                }}
              />
              <StarNode tokenAddress={data.address} className="size-[18px]" />
            </div>
            <div className="text-[12px] flex items-center gap-x-[6px] text-secondaryTextColor">
              <span>{formatTimeAgo(data.createTime, { style: 'shortAgo' })}</span>
              <span>{formatTextEllipsis(data.address)}</span>
              <CopyNode text={data.address} className="size-[18px] opacity-[0.4]" />
            </div>
          </div>
        </div>
        <NoticeSheet noticeInfo={noticeInfo} noticeList={data.alertLogs} />
      </div>
      <TradeSheet tradeList={data.swapLogs} tokenInfo={tokenInfo} />
      <div className="my-[8px]">
        <div className="flex justify-end items-center h-[26px] text-[12px] text-secondaryTextColor pr-[6px] rounded-tl-[8px] rounded-tr-[8px] bg-secondaryBgColor">
          <div className="w-[50px]"></div>
          <div className="flex-1 text-right">{t('Signal.price')}</div>
          <div className="flex-1 text-right">{t('Signal.mc')}</div>
          <div className="flex-1 text-right">{t('Signal.liquidity')}</div>
          <div className="flex-1 text-right">{t('Signal.holders')}</div>
        </div>
        <div className="flex justify-end items-center h-[46px] text-[12px] px-[8px]">
          <div className="w-[50px]">{t('Signal.alert')}</div>
          <div className="flex-1 text-right">${formatCryptoPrice(data.alert.price)}</div>
          <div className="flex-1 text-right">{formatValueWithCompactNumber(alertTimeMc, true)}</div>
          <div className="flex-1 text-right">{alertLiquidity}</div>
          <div className="flex-1 text-right">{Number(data.alert.holders).toLocaleString()}</div>
        </div>
        <div
          className={cn(
            'flex justify-end items-center h-[46px] text-[12px] px-[8px] rounded-tr-[8px] rounded-br-[8px] border-t-[1px] border-[#E4E4E7] dark:border-[#27272A]',
            BigNumber(realTimePrice).gt(data.alert.price) ? 'text-buyColor' : 'text-sellColor',
          )}
          style={{
            backgroundImage: tradeType == 'buy' ? greenGradient : tradeType == 'sell' ? redGradient : '',
          }}
        >
          <div className="w-[50px] !text-primaryTextColor">{t('Signal.now')}</div>
          <div className="flex-1 text-right">${formatCryptoPrice(realTimePrice)}</div>
          <div className="flex-1 text-right">{formatValueWithCompactNumber(nowTimeMc, true)}</div>
          <div className="flex-1 text-right">{realTimeLiquidity}</div>
          <div className="flex-1 text-right">{Number(realTimeHolder).toLocaleString()}</div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-x-[8px] gap-y-[8px]">
        {quickConfig.map((item, index) => {
          return (
            <div
              key={index}
              className={cn(
                'leading-[40px] text-center rounded-[8px] text-white',
                item.type == 'buy' ? 'bg-[#0ECB81cc] dark:bg-[#0ECB811F]' : 'bg-[#f6465dcc] dark:bg-[#F6465D1F]',
              )}
              onClick={(e) => {
                e.stopPropagation();
                onQuickTrade(index, data.address);
              }}
            >
              {item.label}
            </div>
          );
        })}
      </div>
    </div>
  );
};
